import React, { useMemo, useState } from "react";
import { calcBond, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

const freqOptions = [
  { label: "Annual", per: 1 },
  { label: "Semi-Annual", per: 2 },
  { label: "Quarterly", per: 4 },
  { label: "Monthly", per: 12 },
];

export const BondTab = () => {
  const [amount, setAmount] = useState(100000);
  const [coupon, setCoupon] = useState(7.5);
  const [years, setYears] = useState(5);
  const [freqLabel, setFreqLabel] = useState("Semi-Annual");

  const freq = (freqOptions.find((f) => f.label === freqLabel) || freqOptions[1]).per;
  const result = useMemo(() => calcBond(amount, coupon, years, freq), [amount, coupon, years, freq]);
  const animKey = `bond-${Math.round(result.maturity)}-${amount}-${coupon}-${years}-${freq}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <InputRow
          label="Investment Amount"
          valueText={`Rs. ${formatINR(amount)}`}
          valueTestId="bond-amount-value"
          finePrint={<><span>Rs.10,000</span><span>Rs.50,00,000</span></>}
        >
          <Slider value={amount} min={10000} max={5000000} step={5000} onChange={setAmount} ariaLabel="Bond investment" />
        </InputRow>

        <InputRow
          label="Coupon Rate (Annual)"
          valueText={`${coupon.toFixed(2)}% per annum`}
          valueTestId="bond-coupon-value"
          finePrint={<><span>3%</span><span>15% per annum</span></>}
        >
          <Slider value={coupon} min={3} max={15} step={0.05} onChange={setCoupon} ariaLabel="Coupon rate" />
        </InputRow>

        <InputRow
          label="Tenure to Maturity"
          valueText={`${years} ${years === 1 ? "Year" : "Years"}`}
          valueTestId="bond-tenure-value"
          finePrint={<><span>1 year</span><span>30 years</span></>}
        >
          <Slider value={years} min={1} max={30} step={1} onChange={setYears} ariaLabel="Bond tenure" />
        </InputRow>

        <div>
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>
            Coupon Payout Frequency
          </div>
          <Pills items={freqOptions.map((f) => f.label)} value={freqLabel} onChange={setFreqLabel} prefix="bond-freq" />
          <p className="mt-3" style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }}>
            Maturity assumes coupons are reinvested at the same coupon rate and the bond is bought at face value
            and held to maturity. Coupons are taxable at your slab rate.
          </p>
        </div>
      </div>

      <RightPanel testId="bond-results">
        <HeroValue label="Maturity Value" value={`Rs. ${formatINR(result.maturity)}`} animKey={animKey} testId="bond-maturity-value" />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Investment Amount" value={`Rs. ${formatINR(amount)}`} testId="bond-row-principal" />
          <ResultRow label="Coupon per Payout" value={`Rs. ${formatINR(result.periodicCoupon)}`} testId="bond-row-coupon" />
          <ResultRow label="Total Interest (Reinvested)" value={`Rs. ${formatINR(result.interest)}`} valueColor="#1D9E75" testId="bond-row-interest" />
          <ResultRow label="If Not Reinvested" value={`Rs. ${formatINR(result.simpleCouponTotal)}`} valueColor="#F4A700" testId="bond-row-simple" />
          <ResultRow label="Total Value" value={`Rs. ${formatINR(result.maturity)}`} testId="bond-row-total" />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.principalPct}
          rightPct={result.interestPct}
          leftLabel="Principal"
          rightLabel="Interest"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="bond-split-bar"
        />
      </RightPanel>
    </div>
  );
};
